/**
 * opfsAssetStore — local-first, content-addressed image storage in OPFS.
 *
 * Thin facade over the shared @website-builder/blob-store: a draft's images
 * live in an OpfsBlobStore rooted at `sites/<siteId>`, keyed by the SAME served
 * object key as the cloud + the export — `_assets/<sha256>` (no extension; the
 * sha is the identity). Uploads land here first with no network; the bytes
 * sync to the backend later at publish time. The `_synced.json` manifest (a
 * blob in the same bucket) tracks which shas have already been pushed.
 *
 * Keeps its existing API (putAsset / getAssetFile / getSyncedShas / markSynced)
 * so ImageWidget / publishSite / the export are unchanged.
 */
import { OpfsBlobStore } from "@website-builder/blob-store/opfs";
import { assetKey, sha256Hex, ASSET_PREFIX } from "@website-builder/blob-store";

import { extForMime } from "./mimeExt.js";

// Re-exported so existing importers keep these from here.
export { extForMime, sha256Hex };

export interface StoredAsset {
  sha256: string;
  mime: string;
  ext: string;
}

/** The OpfsBlobStore "bucket" for one site/draft. */
function bucket(siteId: string): OpfsBlobStore {
  return new OpfsBlobStore(["sites", siteId]);
}

/** Hash + persist a picked file to OPFS. Returns its content address. */
export async function putAsset(
  siteId: string,
  file: Blob,
): Promise<StoredAsset> {
  const buf = await file.arrayBuffer();
  const sha256 = await sha256Hex(buf);
  const mime = file.type || "application/octet-stream";
  const ext = extForMime(mime);
  await bucket(siteId).put(assetKey(sha256), new Uint8Array(buf));
  return { sha256, mime, ext };
}

/**
 * The stored blob for an asset, or null if not stored locally. The blob is
 * disk-backed/lazy (callers stream it as a fetch body or wrap it in an object
 * URL without reading bytes into memory). The `ext` argument is retained for
 * call-site compatibility but no longer affects the key (the sha is the key).
 * Its `type` is empty; the caller supplies the content-type from stored `mime`.
 */
export async function getAssetFile(
  siteId: string,
  sha256: string,
  _ext?: string,
): Promise<Blob | null> {
  return bucket(siteId).getBlob(assetKey(sha256));
}

/**
 * Every content address stored locally in this site's OPFS bucket — the local
 * half of the Media Library's derived catalog (union with the grid-referenced
 * shas surfaces orphans; see mediaCatalog). Keys are `_assets/<sha>`, so we
 * strip the prefix; non-asset bucket blobs (e.g. `_synced.json`) are excluded
 * by the prefix filter.
 */
export async function listStoredShas(siteId: string): Promise<string[]> {
  const keys = await bucket(siteId).list(ASSET_PREFIX);
  return keys
    .map((k) => k.slice(ASSET_PREFIX.length))
    .filter((sha) => sha.length > 0 && !sha.includes("/"));
}

/** Remove a locally-stored asset (orphan cleanup). No-op if absent. Local only
 *  — never touches published/peer copies another draft may share. */
export async function deleteAsset(
  siteId: string,
  sha256: string,
): Promise<void> {
  await bucket(siteId).delete(assetKey(sha256));
}

/**
 * Copy every asset stored under `fromSiteId` but missing under `toSiteId` into
 * `toSiteId`'s bucket, and merge the synced manifests. Local only; the source
 * copies are left in place. A draft's images live under its own id, but
 * editors before 0.2.25 stored uploads under the cloud id once the draft was
 * online — adopting them back makes those images visible and publishable.
 * Returns how many assets were copied.
 */
export async function adoptAssets(
  fromSiteId: string,
  toSiteId: string,
): Promise<number> {
  if (fromSiteId === toSiteId) return 0;
  const from = bucket(fromSiteId);
  const to = bucket(toSiteId);
  const [theirs, ours] = await Promise.all([
    listStoredShas(fromSiteId),
    listStoredShas(toSiteId),
  ]);
  const have = new Set(ours);
  let copied = 0;
  for (const sha of theirs) {
    if (have.has(sha)) continue;
    const bytes = await from.get(assetKey(sha));
    if (!bytes) continue;
    await to.put(assetKey(sha), bytes);
    copied += 1;
  }
  const synced = await getSyncedShas(fromSiteId);
  if (synced.size > 0) await markSynced(toSiteId, [...synced]);
  return copied;
}

// --- sync manifest: shas already uploaded to the backend ---

const SYNCED_KEY = "_synced.json";

export async function getSyncedShas(siteId: string): Promise<Set<string>> {
  const bytes = await bucket(siteId).get(SYNCED_KEY);
  if (!bytes) return new Set();
  try {
    const parsed = JSON.parse(new TextDecoder().decode(bytes)) as unknown;
    return new Set(Array.isArray(parsed) ? (parsed as string[]) : []);
  } catch {
    return new Set();
  }
}

export async function markSynced(
  siteId: string,
  shas: readonly string[],
): Promise<void> {
  const merged = await getSyncedShas(siteId);
  for (const sha of shas) merged.add(sha);
  await bucket(siteId).put(
    SYNCED_KEY,
    new TextEncoder().encode(JSON.stringify([...merged])),
  );
}
